<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';
import Panel from 'primevue/panel';
import { AssociationAccount } from '@apparatix/types/Leads';
import AssociationListItem from './AssociationListItem.vue';
import ContactTypeChip from '@apparatix/leads/ContactTypeChip.vue';
import ExclamationTriangleIcon from '@primeicons/vue/exclamation-triangle';

interface AccountConflictProps {
  conflicts: Array<AssociationAccount & { isHardConflict?: boolean }>;
  allowDismiss?: boolean;
  selectButtonLabel?: string;
  dismissButtonLabel?: string;
}

const props = withDefaults(defineProps<AccountConflictProps>(), {
  allowDismiss: true,
  selectButtonLabel: 'Select',
  dismissButtonLabel: 'Dismiss Suggestions',
});

const emit = defineEmits<{
  (e: 'account-selected', account: AssociationAccount): void;
  (e: 'cancel'): void;
}>();

const hardConflicts = computed(() => props.conflicts.filter(account => account.isHardConflict));
const potentialConflicts = computed(() => props.conflicts.filter(account => !account.isHardConflict));

const getAddress = (account: AssociationAccount) => {
  if (!account?.addresses?.length) return '';
  const [address] = account.addresses;
  return `${address.address_line1 ?? ''}, ${address.city ?? ''}, ${address.region ?? ''} ${address.postal_code ?? ''}`;
};
</script>
<template>
  <div v-if="props.conflicts?.length > 0" class="flex flex-col w-full gap-6">
    <h2 class="font-medium text-base text-[var(--swimlane-secondary-text)]">
      <ExclamationTriangleIcon /> The Account you&rsquo;re creating might be a duplicate
    </h2>
    <Panel v-if="hardConflicts.length > 0" header="Duplicate Accounts">
      <AssociationListItem
        v-for="account in hardConflicts"
        :key="account.c2Id"
        association-type="account"
        :title="account.c2Name"
      >
        <div class="flex items-center justify-end">
          <Button
            severity="secondary"
            outlined
            @click="emit('account-selected', account)"
          >
            Select
          </Button>
        </div>
        <template #footer>
          <div class="w-auto">
            <ContactTypeChip size="small" :label="account.c2Type" />
          </div>
        </template>
      </AssociationListItem>
    </Panel>
    <Panel v-if="potentialConflicts.length > 0" header="Potentially duplicate Accounts">
      <div class="flex flex-col gap-4">
        <AssociationListItem
          v-for="(account, idx) in potentialConflicts"
          :key="account.c2Id"
          association-type="account"
          :title="account.c2Name"
          :subtitle="getAddress(account)"
          :border="idx !== potentialConflicts.length - 1"
        >
          <div class="flex items-center justify-end">
            <div v-tooltip.left="account.isAllowedSelection ? '' : `You cannot select this Account because it is owned by ${account.ownerName}`" class="inline-flex">
              <Button
                severity="secondary"
                :disabled="!account.isAllowedSelection"
                outlined
                @click="emit('account-selected', account)"
              >
                {{ props.selectButtonLabel }}
              </Button>
            </div>
          </div>
          <template #footer>
            <div class="w-auto">
              <ContactTypeChip size="small" :label="account.c2Type" />
            </div>
          </template>
        </AssociationListItem>
      </div>
    </Panel>
    <div v-if="props.allowDismiss" class="flex items-center">
      <Button
        severity="secondary"
        outlined
        @click="emit('cancel')"
      >
        {{ dismissButtonLabel }}
      </Button>
    </div>
  </div>
</template>
